import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiContext } from "../../context/ApiContext";
import useStepData from "../../hooks/useStepData";
import Button from "../../shared/Button";
import Loader from "../../shared/Loader";
import Timeline from "../../shared/Timeline";
import __ from "../../utils/getContent";
import getClubSlugFromName from "../../utils/getClubSlugFromName";
import CheckboxField from "../fields/checkboxField";
import ContractInterstitial from "./contractInterstitial";

// Augment the global Window interface so TypeScript knows about the third-party widget
declare global {
	interface Window {
		$zigchat?: {
			setUser?: (opts: { email?: string; token?: string }) => void;
		};
		midtownMembershipsSettings?: {
			zigment_widget_id?: "";
			quoted_rates_rules?: Array<{
				club_id?: string | number;
				quoted_rates_html?: string;
			}>;
		};
	}
}

function resolveQuotedRatesHtml(
	settings: {
		quoted_rates_rules?: Array<{
			club_id?: string | number;
			quoted_rates_html?: string;
		}>;
	} | undefined,
	clubId: string | number | undefined,
	defaultHtml: string,
): string {
	const rules = settings?.quoted_rates_rules ?? [];
	const cid = String(clubId ?? "");
	const matched = Array.isArray(rules)
		? rules.find((row) => String(row.club_id) === cid)
		: undefined;
	const html = matched?.quoted_rates_html?.trim();
	return html || defaultHtml;
}

const StepPricing = (): React.JSX.Element => {
	const { data, setData } = useContext(ApiContext);
	const { error, loading, stepData } = useStepData(2);
	const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
	const navigate = useNavigate();

	const selectMembershipType = (type: any) => {
		if (!type.isAvailable && type.WaitlistLink) {
			window.open(type.WaitlistLink, "_blank");
			return;
		}

		setData({
			...data,
			selectedMembership: type,
		});

		// If Montreal club, the interstitial handles moving to next step
		if (data?.selectedClub?.State === "QC") {
			setIsTermsModalOpen(true);
			return;
		}

		navigate("?step=summary");
	};

	const closeModal = () => {
		setIsTermsModalOpen(false);
	};

	// Handle Zigment live chat widget.
	useEffect(() => {
		if (
			!data?.contact_id?.email ||
			!data?.contact_id?.zigmentEnabled ||
			!data?.contact_id?.zigmentToken
		) {
			return; // Exit if missing email or zigment config.
		}

		// Get widget ID from global settings.
		const widgetid = window?.midtownMembershipsSettings?.zigment_widget_id;
		if (!widgetid) {
			// Note: This will only work in the WordPress environment where the settings are defined.
			console.warn("Missing Zigment widget ID");
			return; // Exit if widget ID missing.
		}

		const script = document.createElement("script");
		script.src = `https://cdn.zigment.ai/widget/load.js?id=${widgetid}`;
		script.async = true;
		document.body.appendChild(script);

		const setZigmentUser = () => {
			setTimeout(() => {
				// Add slight delay as per Zigment docs.
				const zig = window?.$zigchat;
				if (zig && typeof zig.setUser === "function") {
					zig.setUser?.({
						email: data.contact_id.email,
						token: data.contact_id.zigmentToken,
					});
				}
			}, 1500);
		};

		script.addEventListener("load", setZigmentUser);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	if (loading) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<Loader colorCircle="#000" colorRotator="#ddd" />
			</div>
		);
	}

	if (error) {
		navigate("?step=contact&error=1");
	}

	if (isTermsModalOpen) {
		return <ContractInterstitial closeModal={closeModal} />;
	}

	const clubSlug = getClubSlugFromName(data?.selectedClub?.Name);

	const quotedRatesHtml = resolveQuotedRatesHtml(
		window.midtownMembershipsSettings,
		data?.get_club_id,
		__(
			"quoted_rates",
			"Quoted rates are for new members. Please <a href=''>contact us</a> with additional questions.",
		),
	);

	return (
		<>
			<Timeline currentStep={2} />

			<div className="step-pricing mb-10 text-center">
				<h1 className="mb-1 text-3xl font-bold uppercase">
					{__("select_membership_plan", "Select Membership Plan")}
				</h1>

				{data?.selectedClub?.Name && (
					<h2 className="location-name mb-2 text-lg font-bold">
						{data.selectedClub.Name}
					</h2>
				)}

				<p
					className="mb-6"
					dangerouslySetInnerHTML={{
						__html: quotedRatesHtml,
					}}
				></p>

				{412 === data?.get_club_id && (
					<>
						<div className="mx-auto inline font-bold">
							<CheckboxField
								registeredName="splitPayment"
								isRequired={false}
								label={__(
									"split_payment_label",
									"Split my initiation fee into 2 payments.",
								)}
								onChange={(event) => {
									setData({
										...data,
										splitPayment: event.target.checked,
									});
								}}
								wrapperClass="justify-center"
							/>
						</div>
						<p className="mb-10 text-xs">
							{__(
								"split_payment_details",
								"Pay 50% of your initiation fee today and the remaining 50% in 7 months.",
							)}
						</p>
					</>
				)}

				<div className="mb-6 flex flex-wrap justify-center">
					{stepData?.get_membership_types?.MembershipTypes &&
						stepData?.get_membership_types?.MembershipTypes.map((type: any) => (
							<MembershipItem
								key={type.Name}
								membershipType={type}
								clubSlug={clubSlug}
								handleClick={() => {
									selectMembershipType(type);
								}}
							/>
						))}
				</div>				
			</div>
		</>
	);
};

const MembershipItem = ({
	membershipType,
	clubSlug,
	handleClick,
}: {
	membershipType: any;
	clubSlug?: string;
	handleClick: React.MouseEventHandler;
}) => {
	const tourBaseUrl = __(
		"button_hyperlink_club_tour",
		"https://www.midtown.com/schedule-a-tour/",
	);

	return (
		<div className="m-3 flex w-60 flex-col justify-between border border-black">
			<div>
				<h3 className="mb-5 border-b border-solid border-black py-3 text-lg font-bold">
					{membershipType.Name}
				</h3>

				<div className="flex flex-col px-5">
					<p className="mb-2 text-5xl">
						${membershipType.Rate}
						<span className="text-2xl">/mo</span>
					</p>

					<p className="mb-5 italic">
						+${membershipType.InitiationFees}{" "}
						{__("enrollment_fee", "enrollment fee")}
					</p>

					<p className="mb-5 px-2">
						{membershipType.isAvailable
							? membershipType.Description
							: membershipType.WaitlistDescription}
					</p>
				</div>
			</div>


			<div className="flex flex-col items-center gap-3 px-5 py-5">
				<Button
					action={() => {
						const tourUrl = clubSlug
							? `${tourBaseUrl.replace(/\/?$/, "/")}${clubSlug}/`
							: tourBaseUrl;

						window.open(tourUrl, "_blank");
					}}
					cssClass="self-center"
					buttonType="button"
				>
					{__("button_text_club_tour" , "Schedule a Tour")}
				</Button>

				<Button
					action={() => {
						handleClick(membershipType);
					}}
					cssClass="self-center"
					buttonType="button"
					variant="secondary"
				>
					{membershipType.EnrollmentLinkText || "Join Now"}
				</Button>
			</div>
		</div>
	);
};

export default StepPricing;
